import {
  ActionLink,
  LearningShell,
  ToneBadge,
} from '../features/learning-space/components';
import { useSearchParams } from 'react-router-dom';
import { buildLearningRouteScope } from '@examflow/learning-space';
import {
  buildMockExamPageModel,
  resolveMockExamPageStatus,
} from '@examflow/learning-space';
import { useLearningViewer } from '../features/learning-space/useLearningViewer';
import {
  buildLearningEvidenceRecordQueryScope,
  useRecentTrainingAttempts,
} from '../features/learning-space/useRecentTrainingAttempts';
import { useAccessibleBanks } from '../hooks/useApiQueries';
import { buildPlatformScope } from '@examflow/core';
import {
  formatExamSpaceIdentityLabel,
  resolveExamSpaceTheme,
} from '@examflow/learning-space';

export default function ExamMockExamPage() {
  const [searchParams] = useSearchParams();
  const viewer = useLearningViewer(buildLearningRouteScope(searchParams));
  const activeScope = buildPlatformScope({ examType: viewer.activeExamSpace.examCode });
  const theme = resolveExamSpaceTheme(viewer.activeExamSpace.examCode);
  const identityLabel = formatExamSpaceIdentityLabel(
    viewer.activeExamSpace.examCode,
    viewer.activeExamSpace.examName
  );
  const evidenceScope = buildLearningEvidenceRecordQueryScope(viewer);
  const trainingAttempts = useRecentTrainingAttempts({
    userId: viewer.userId,
    ...evidenceScope,
    limit: 30,
  });
  const accessibleBanks = useAccessibleBanks(activeScope ?? undefined);
  const status = resolveMockExamPageStatus({
    records: trainingAttempts.records,
    examCode: viewer.activeExamSpace.examCode,
  });
  const model = buildMockExamPageModel(viewer, status, trainingAttempts.records, {
    accessibleBanks: accessibleBanks.data,
    accessibleBanksStatus: accessibleBanks.isLoading
      ? 'loading'
      : accessibleBanks.isError
        ? 'unavailable'
        : 'ready',
  });
  const canStart = model.availability.canStartFullMock;
  const secondaryAction =
    model.secondaryAction.kind === 'primary'
      ? { ...model.secondaryAction, kind: 'ghost' as const }
      : model.secondaryAction;

  return (
    <LearningShell viewer={viewer} activeNav="mock-exam" statusLabel={model.stateCard.badge}>
      <section data-testid="exam-space-mock-hero">
        <p className="text-xs font-semibold tracking-[0.14em]" style={{ color: theme.accent }}>
          {identityLabel}
        </p>
        <h1
          className="mt-3 max-w-3xl font-black tracking-tight text-[#111827]"
          style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', lineHeight: 1.08 }}
        >
          模拟考试
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
          {canStart
            ? `按 ${viewer.activeExamSpace.examName} 全真顺序作答。交卷后写入能力诊断。`
            : model.stateCard.body}
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <ActionLink action={model.primaryAction} />
          <ActionLink action={secondaryAction} />
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-slate-600">
          <ToneBadge tone={model.stateCard.tone}>{model.stateCard.badge}</ToneBadge>
          <span>{canStart ? '可以开始全真模考' : '先补齐训练条件再开考'}</span>
        </div>
      </section>

      {model.availability.gateReasons.length > 0 && (
        <section className="mt-10" data-testid="exam-space-mock-gates">
          <h2 className="mb-3 text-[11px] font-bold tracking-[0.18em] text-slate-500">开考条件</h2>
          <ul className="divide-y divide-slate-200/70 rounded-md border border-white/70 bg-white/55">
            {model.availability.gateReasons.map((reason) => (
              <li
                key={reason.code}
                className="flex items-center justify-between gap-4 px-4 py-3 text-sm"
              >
                <span className="font-medium text-[#111827]">{reason.label}</span>
                <span className="shrink-0 font-mono text-slate-500">
                  {reason.currentValue}
                  {reason.requiredValue ? ` / ${reason.requiredValue}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-10" data-testid="exam-space-mock-modules">
        <div className="mb-3 flex items-baseline justify-between gap-4">
          <h2 className="text-[11px] font-bold tracking-[0.18em] text-slate-500">考试结构</h2>
          <p className="text-[11px] text-slate-500">全真模块顺序</p>
        </div>
        <ol className="space-y-2">
          {model.modules.map((module, index) => (
            <li
              key={module.moduleId}
              className="flex items-center justify-between gap-4 rounded-md border border-white/70 bg-white/55 px-4 py-3 text-sm"
            >
              <span className="font-semibold text-[#111827]">
                <span className="mr-3 font-mono text-xs text-slate-400">{index + 1}</span>
                {module.label}
              </span>
              <span className="text-slate-500">
                {module.durationMinutes} 分钟 · {module.questionCount} 题
              </span>
            </li>
          ))}
        </ol>
        {model.contentReadiness.sectionReadiness.length > 0 && (
          <p className="mt-4 text-xs leading-5 text-slate-500">
            内容覆盖：
            {model.contentReadiness.sectionReadiness
              .map(
                (section) =>
                  `${section.label}${section.status === 'ready' ? '已就绪' : '待补齐'}`
              )
              .join(' · ')}
          </p>
        )}
      </section>
    </LearningShell>
  );
}
